/**
 * SocialShare - Share completed trips to Instagram & TikTok
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, Copy, Check, Download, ExternalLink } from "lucide-react";
import { shareToApp } from "@/hooks/useNativeShare";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export const SocialShare = ({ item, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!item) return null;

  const shareText = generateShareText(item);
  const hashtags = generateHashtags(item);
  const profileUrl = `${window.location.origin}/u/${item.username || 'duolyst'}`;

  const handleCopyCaption = async () => {
    const fullText = `${shareText}\n\n${hashtags}`;
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Caption copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleDownloadImage = async () => {
    setDownloading(true);
    try {
      // If item has photos, download the first one
      if (item.photos && item.photos.length > 0) {
        const response = await fetch(item.photos[0].url);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `duolyst-${item.title.replace(/\s+/g, '-').toLowerCase()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Image downloaded!");
      } else {
        toast.info("No photos to download");
      }
    } catch (error) {
      toast.error("Failed to download image");
    } finally {
      setDownloading(false);
    }
  };

  const openInstagram = () => {
    handleCopyCaption();
    shareToApp("instagram", shareText + "\n\n" + hashtags);
    toast.info("Caption copied! Paste it in your Instagram post");
  };

  const openTikTok = () => {
    handleCopyCaption();
    shareToApp("tiktok", shareText + "\n\n" + hashtags);
    toast.info("Caption copied! Paste it in your TikTok post");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card border-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            Share Your Adventure
          </DialogTitle>
          <DialogDescription>
            Share "{item.title}" to social media
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Preview Card */}
          <div className="p-4 bg-muted/50 rounded-xl border border-charcoal/5">
            <div className="flex items-start gap-3">
              {item.photos && item.photos.length > 0 ? (
                <img 
                  src={item.photos[0].url} 
                  alt={item.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-2xl">
                  {getCategoryEmoji(item.category)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-charcoal truncate">{item.title}</h4>
                {item.location && (
                  <p className="text-sm text-muted-foreground truncate">
                    📍 {item.location}{item.country && `, ${item.country}`}
                  </p>
                )}
                <p className="text-xs text-green-600 mt-1">✓ Completed</p>
              </div>
            </div>
          </div>

          {/* Caption Preview */}
          <div className="p-3 bg-background rounded-lg border border-charcoal/10">
            <p className="text-sm text-charcoal whitespace-pre-line">{shareText}</p>
            <p className="text-sm text-primary mt-2">{hashtags}</p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {/* Instagram */}
            <Button
              onClick={openInstagram}
              className="w-full h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white"
            >
              <Instagram className="w-5 h-5 mr-2" />
              Share to Instagram
              <ExternalLink className="w-4 h-4 ml-auto" />
            </Button>

            {/* TikTok */}
            <Button
              onClick={openTikTok}
              className="w-full h-12 bg-black hover:bg-black/90 text-white"
            >
              <TikTokIcon className="w-5 h-5 mr-2" />
              Share to TikTok
              <ExternalLink className="w-4 h-4 ml-auto" />
            </Button>

            {/* Copy & Download */}
            <div className="flex gap-2">
              <Button
                onClick={handleCopyCaption}
                variant="outline"
                className="flex-1 h-10"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Caption
                  </>
                )}
              </Button>
              
              {item.photos && item.photos.length > 0 && (
                <Button
                  onClick={handleDownloadImage}
                  variant="outline"
                  className="flex-1 h-10"
                  disabled={downloading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? "..." : "Download Photo"}
                </Button>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Tip:</strong> The caption has been optimized for engagement. 
              Add your own personal touch before posting!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper functions
function generateShareText(item) {
  const templates = [
    `Just crossed "${item.title}" off my bucket list! ✨`,
    `Dream achieved: ${item.title} ✅`,
    `Another adventure complete! ${item.title}`,
    `Living the dream! Just did: ${item.title}`,
  ];
  
  let text = templates[Math.floor(Math.random() * templates.length)];
  
  if (item.location) {
    text += `\n\n📍 ${item.location}${item.country ? `, ${item.country}` : ''}`;
  }
  
  text += `\n\nTracking my adventures with Duolyst.co.uk 🌍`;
  
  return text;
}

function generateHashtags(item) {
  const baseHashtags = ['#bucketlist', '#duolyst', '#adventure', '#travel'];
  const categoryHashtags = {
    travel: ['#wanderlust', '#travelgram', '#explore'],
    experiences: ['#experience', '#memories', '#livingmybestlife'],
    adventure: ['#adventuretime', '#thrill', '#adrenaline'],
    food: ['#foodie', '#foodlover', '#culinary'],
    romance: ['#couplegoals', '#love', '#romantic'],
    goals: ['#goals', '#achievement', '#dreams'],
  };
  
  const tags = [...baseHashtags];
  
  if (item.category && categoryHashtags[item.category]) {
    tags.push(...categoryHashtags[item.category]);
  }
  
  if (item.country) {
    const countryTag = `#${item.country.replace(/\s+/g, '').toLowerCase()}`;
    tags.push(countryTag);
  }
  
  return tags.slice(0, 10).join(' ');
}

function getCategoryEmoji(category) {
  const emojis = {
    travel: '✈️',
    experiences: '🎭',
    adventure: '🏔️',
    food: '🍽️',
    romance: '💕',
    goals: '🎯',
  };
  return emojis[category] || '✨';
}

export default SocialShare;
